import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, FormControl, InputLabel, Select, MenuItem, Button, FormControlLabel, Switch, Typography, Paper, IconButton, List, ListItem, ListItemText, Divider, TextField } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import config from '../config';

import Sidebar from '../components/Sidebar';
import { PageContainer, ContentContainer, Header } from '../components/ContentContainer';

const PomSceneAddPage = () => {
  const navigate = useNavigate();
  const [productType, setProductType] = useState('');
  const [wristWidth, setWristWidth] = useState('');
  const [renderMode, setRenderMode] = useState('');
  const [sceneFile, setSceneFile] = useState(null);
  const [productFile, setProductFile] = useState(null);
  const [useDefaultProduct, setUseDefaultProduct] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestPreview, setRequestPreview] = useState('');
  const [lastSessionId, setLastSessionId] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionImagesQueryUrl, setSessionImagesQueryUrl] = useState('');
  const [sessionFsBaseAbs, setSessionFsBaseAbs] = useState('');
  const [sessionFsTargetAbs, setSessionFsTargetAbs] = useState('');
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagesError, setImagesError] = useState('');
  const [apiBase, setApiBase] = useState('');
  const [procImages, setProcImages] = useState([]);
  const [procSelected, setProcSelected] = useState(null);
  const [procError, setProcError] = useState('');
  const [procLoading, setProcLoading] = useState(false);
  const [procVersion, setProcVersion] = useState(0);
  const [points, setPoints] = useState([]);
  const [selectedPointId, setSelectedPointId] = useState(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const [imgNaturalSize, setImgNaturalSize] = useState({ w: 0, h: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragPointId, setDragPointId] = useState(null);
  const [uploadedImageFile, setUploadedImageFile] = useState(null);
  const [uploadedImagePreviewUrl, setUploadedImagePreviewUrl] = useState(null);
  const [holdoutSrc, setHoldoutSrc] = useState(null);
  const uploadContainerRef = useRef(null);
  const [holdoutNaturalRatio, setHoldoutNaturalRatio] = useState(1); // width/height
  const [holdoutNaturalSize, setHoldoutNaturalSize] = useState({ w: 0, h: 0 });
  const [overlayBoxPx, setOverlayBoxPx] = useState({ left: 50, top: 50, width: 200 });
  const [isOverlayDragging, setIsOverlayDragging] = useState(false);
  const [activeResizeHandle, setActiveResizeHandle] = useState(null); // 'nw' | 'ne' | 'se' | 'sw'
  const resizeDragStartRef = useRef(null); // { startX, startY, startLeft, startTop, startWidth, startHeight, containerRect, type, handle }
  const [baseImageNaturalSize, setBaseImageNaturalSize] = useState({ w: 0, h: 0 });
  const [baseImageDisplaySize, setBaseImageDisplaySize] = useState({ w: 0, h: 0 });
  const [sceneName, setSceneName] = useState('');
  const [sceneIndex, setSceneIndex] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [postprocPadding, setPostprocPadding] = useState(0.2);

  // Load session images (from the render button initiated session)
  useEffect(() => {
    // On session change, clear list and instruct to click "Show images"
    if (!currentSessionId) {
      setImages([]);
      setSelectedImage(null);
      setImagesError('Run Render to see images for the new session.');
      return;
    }
    setImages([]);
    setSelectedImage(null);
    setImagesError('Click "Show images" after render completes.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId]);

  const handleShowImages = async () => {
    if (!currentSessionId) {
      setImagesError('No session yet. Run Render first.');
      return;
    }
    try {
      const q = new URLSearchParams({ session_id: currentSessionId }).toString();
      const base = apiBase || config.serverUrl;
      const url = `${base}/session-images?${q}`;
      // eslint-disable-next-line no-console
      console.log('[Show images] GET', url, { session: currentSessionId });
      const resp = await fetch(url, { method: 'GET' });
      // eslint-disable-next-line no-console
      console.log('[Show images] Response status', resp.status);
      if (!resp.ok) {
        setImagesError(`Failed to load images (${resp.status})`);
        return;
      }
      const data = await resp.json();
      // eslint-disable-next-line no-console
      console.log('[Show images] Data', data);
      setSessionImagesQueryUrl(url);
      if (data.base_abs) setSessionFsBaseAbs(data.base_abs);
      if (data.target_abs) setSessionFsTargetAbs(data.target_abs);
      const files = Array.isArray(data.files) ? data.files : [];
      const absoluteFiles = files.map((p) => {
        try {
          if (!p) return p;
          const isAbs = /^https?:\/\//i.test(p);
          if (isAbs) return p;
          if (p.startsWith('/')) {
            return `${base}${p}`;
          }
          return new URL(p, base + '/').href;
        } catch {
          return p;
        }
      });
      setImages(absoluteFiles);
      setSelectedImage(absoluteFiles[0] || null);
      // eslint-disable-next-line no-console
      console.log('[Show images] Loaded files', { count: absoluteFiles.length, sample: absoluteFiles.slice(0, 5) });
      setImagesError(absoluteFiles.length ? '' : 'No POM images found for this session yet.');
    } catch (e) {
      setImagesError('Error loading session images');
    }
  };

  // Helper to load existing postprocessed images (GET only)
  const loadPostprocessed = async () => {
    if (!currentSessionId) {
      setProcImages([]);
      setProcSelected(null);
      setProcError('');
      return;
    }
    try {
      const q = new URLSearchParams({ session_id: currentSessionId }).toString();
      const candidatesBase = [
        '',
        config.serverUrl,
        'http://127.0.0.1:8000',
        config.serverUrl || ''
      ];
      let data = null;
      for (const base of candidatesBase) {
        try {
          const url = `${base}/session-images-postproc?${q}&_t=${Date.now()}`;
          // eslint-disable-next-line no-console
          console.log('[Postprocess] Load images GET', url);
          const resp = await fetch(url, { method: 'GET', cache: 'no-store' });
          if (!resp.ok) continue;
          data = await resp.json();
          // eslint-disable-next-line no-console
          console.log('[Postprocess] Load images data', data);
          break;
        } catch {
          continue;
        }
      }
      const files = data && Array.isArray(data.files) ? data.files : [];
      const toAbs = (u) => {
        try {
          if (!u) return u;
          // Already absolute URL
          if (/^https?:\/\//i.test(u)) return u;
          const base = apiBase || config.serverUrl || '';
          // If path starts with '/', append to base (preserving /render-service/ prefix)
          if (u.startsWith('/')) {
            return base + u;
          }
          return new URL(u, base + '/').href;
        } catch {
          return u;
        }
      };
      const absolute = files.map(toAbs);
      setProcImages(absolute);
      setProcSelected(absolute[0] || null);
      setProcVersion(Date.now());
      // eslint-disable-next-line no-console
      console.log('[Postprocess] Loaded postprocessed files', { count: absolute.length, sample: absolute.slice(0, 5) });
      setProcError(absolute.length ? '' : '');
    } catch {
      setProcError('Failed to load postprocessed images');
    }
  };

  // Load existing postprocessed images when backend base or session changes
  useEffect(() => {
    loadPostprocessed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, currentSessionId]);

  // Button handler to run postprocess (POST) and then reload images
  const handlePostprocess = async () => {
    if (!currentSessionId) {
      setProcError('No session yet. Run Render first.');
      return;
    }
    try {
      setProcLoading(true);
      // Clear current postprocessed state before running
      setProcError('');
      setProcImages([]);
      setProcSelected(null);
      setProcVersion(Date.now());
      const q = new URLSearchParams({ session_id: currentSessionId }).toString();
      const base = apiBase || config.serverUrl;
      const url = `${base}/session-postprocess?${q}`;
      // Build params payload mapping: overlay image anchors -> labeled points on base image
      // Base (destination) = labeled points on original image (absolute pixels)
      const pixelPoints = points.slice(0, 2).map((p) => ([
        Math.round(p.x * (imgNaturalSize.w || 0)),
        Math.round(p.y * (imgNaturalSize.h || 0))
      ]));
      const cat =
        productType === 'rings' ? 'Jewelry' :
          productType === 'necklaces' ? 'Necklaces' :
            productType === 'watches' ? 'Watches' :
              productType === 'bracelets' ? 'Bracelets' :
                productType === 'earrings' ? 'Earrings' : 'Jewelry';
      // Overlay (source) = anchors inside the overlay (holdout) image in its own pixels
      const holdW = holdoutNaturalSize.w || 0;
      const holdH = holdoutNaturalSize.h || 0;
      const overlayAnchors = holdW && holdH
        ? [[0, Math.round(holdH / 2)], [holdW, Math.round(holdH / 2)]]
        : [[0, 0], [0, 0]];
      // Map overlay anchors to absolute base image pixels considering drag/resize
      const dispW = baseImageDisplaySize.w || 0;
      const dispH = baseImageDisplaySize.h || 0;
      const natW = baseImageNaturalSize.w || 0;
      const natH = baseImageNaturalSize.h || 0;
      const scaleX = dispW ? (natW / dispW) : 1;
      const scaleY = dispH ? (natH / dispH) : 1;
      const overlayWdisp = overlayBoxPx.width || 0;
      const overlayHdisp = (overlayBoxPx.width || 0) / (holdoutNaturalRatio || 1);
      const leftDisp = overlayBoxPx.left || 0;
      const topDisp = overlayBoxPx.top || 0;
      const sx = holdW ? (overlayWdisp / holdW) : 0;
      const sy = holdH ? (overlayHdisp / holdH) : 0;
      const mappedAnchors = overlayAnchors.map(([ox, oy]) => {
        const xd = leftDisp + ox * sx;
        const yd = topDisp + oy * sy;
        return [Math.round(xd * scaleX), Math.round(yd * scaleY)];
      });
      // Map labeled points' relative coords inside the overlay box to absolute base image pixels
      const mappedLabeled = points.slice(0, 2).map((p) => {
        const xd = leftDisp + (p?.x || 0) * overlayWdisp;
        const yd = topDisp + (p?.y || 0) * overlayHdisp;
        return [Math.round(xd * scaleX), Math.round(yd * scaleY)];
      });
      // Prepare base param item and include a name for non-ring categories
      const nameKey = (sceneName && String(sceneIndex)) ? `${(sceneName || '').trim()}_${String(parseInt(String(sceneIndex), 10))}` : undefined;
      const paramItem = nameKey && cat !== 'Jewelry'
        ? { category: cat, name: nameKey }
        : { category: cat };
      if (cat === 'Jewelry') {
        // Rings: keep base labeled points and include padding
        paramItem.points = pixelPoints;
        const pad = Number.isFinite(Number(postprocPadding)) ? Number(postprocPadding) : 0.2;
        paramItem.padding = pad;
      } else {
        // Non-ring categories
        paramItem.points = pixelPoints;
        paramItem.padding = 0;
        if (cat === 'Earrings') {
          // For earrings: sticky point is first labeled point (relative), map it to absolute px for product_placement_coordinate
          const sp = points[0] ? [Number((points[0].x).toFixed(6)), Number((points[0].y).toFixed(6))] : [0, 0];
          const firstMapped = (mappedLabeled && mappedLabeled.length > 0) ? mappedLabeled[0] : [0, 0];
          paramItem.sticky_point = sp;
          paramItem.product_placement_coordinate = firstMapped;
          const scl = 0.7;
          paramItem.scale = scl;
        } else if (cat === 'Bracelets' || cat === 'Watches' || cat === 'Necklaces') {
          // For bracelets/watches/necklaces: two-point placement
          paramItem.product_placement_coordinates = mappedLabeled;
          if (cat === 'Necklaces' && holdW && holdH) {
            // Also include product anchors for necklaces
            paramItem.necklace_points = [
              [0, Math.round(holdH / 2)],
              [holdW, Math.round(holdH / 2)]
            ];
          }
        } else {
          // Fallback: use base-image labeled points in px
          paramItem.product_placement_coordinates = pixelPoints;
        }
      }
      const payload = { params: [paramItem] };
      // eslint-disable-next-line no-console
      console.log('[Postprocess] POST', url, { session: currentSessionId, payload });
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      // eslint-disable-next-line no-console
      console.log('[Postprocess] Response status', resp.status);
      try {
        const respText = await resp.clone().text();
        // eslint-disable-next-line no-console
        console.log('[Postprocess] Response body (debug)', respText.slice(0, 500));
      } catch { }
      if (!resp.ok) {
        setProcError(`Failed to postprocess. (${resp.status})`);
        setProcImages([]);
        setProcSelected(null);
        return;
      }
      // Single refresh of postprocessed images after successful postprocess
      await loadPostprocessed();
    } finally {
      setProcLoading(false);
    }
  };
  const handleSaveScene = async () => {
    const isWristCategory = productType === 'watches' || productType === 'bracelets';
    if (!uploadedImageFile || !sceneFile || !sceneName || !sceneName.trim() || !sceneIndex) {
      setSaveMessage('Please provide: uploaded image, scene file, scene name and index.');
      return;
    }
    if (isWristCategory && !wristWidth) {
      setSaveMessage('Please select wrist width for watches/bracelets before saving.');
      return;
    }
    try {
      setSaving(true);
      setSaveMessage('');
      const base = apiBase || config.serverUrl;
      const url = `${base}/save-scene`;
      const pts = points.map((p) => ([
        Math.round(p.x * (imgNaturalSize.w || 0)),
        Math.round(p.y * (imgNaturalSize.h || 0))
      ]));
      // Compute placement depending on product type
      const natW = baseImageNaturalSize.w || 0;
      const natH = baseImageNaturalSize.h || 0;
      const dispW = baseImageDisplaySize.w || 0;
      const dispH = baseImageDisplaySize.h || 0;
      const scaleX = dispW ? (natW / dispW) : 1;
      const scaleY = dispH ? (natH / dispH) : 1;
      const overlayWdisp = overlayBoxPx.width || 0;
      const overlayHdisp = (overlayBoxPx.width || 0) / (holdoutNaturalRatio || 1);
      const leftDisp = overlayBoxPx.left || 0;
      const topDisp = overlayBoxPx.top || 0;
      const holdW = holdoutNaturalSize.w || 0;
      const holdH = holdoutNaturalSize.h || 0;
      const sx = holdW ? (overlayWdisp / holdW) : 0;
      const sy = holdH ? (overlayHdisp / holdH) : 0;
      let placement = points.slice(0, 2).map((p) => ([
        Math.round(p.x * natW),
        Math.round(p.y * natH)
      ]));
      if (productType === 'rings' || productType === 'bracelets' || productType === 'necklaces' || productType === 'watches' || productType === 'earrings') {
        placement = points.slice(0, 2).map((p) => {
          const xd = leftDisp + (p?.x || 0) * overlayWdisp;
          const yd = topDisp + (p?.y || 0) * overlayHdisp;
          return [Math.round(xd * scaleX), Math.round(yd * scaleY)];
        });
      }
      // Build JSON object per product type
      let obj = {};
      if (productType === 'rings') {
        const padLocal = Number.isFinite(Number(postprocPadding)) ? Number(postprocPadding) : 0.2;
        obj = {
          points: pts,
          padding: padLocal,
          category: 'Jewelry',
          img_path: (sceneName && sceneName.trim()) ? `data/scenes/${sceneName.trim()}.webp` : '',
          product_placement_coordinates: placement
        };
      } else if (productType === 'bracelets') {
        obj = {
          points: pts,
          category: 'Bracelets',
          size: parseInt(String(wristWidth || '0'), 10) || 0,
          img_path: (sceneName && sceneName.trim()) ? `data/scenes/${sceneName.trim()}.webp` : '',
          product_placement_coordinates: placement
        };
      } else if (productType === 'necklaces') {
        obj = {
          necklace_points: pts,
          padding: 0,
          sticky_point: [0.5, 0.25],
          category: 'Necklaces',
          img_path: (sceneName && sceneName.trim()) ? `data/scenes/${sceneName.trim()}.webp` : '',
          product_placement_coordinates: placement
        };
      }
      else {
        if (productType === 'earrings') {
          // Calculate scale: how much the product (holdout) is scaled on the scene image
          // overlayWdisp = width of product box in screen pixels
          // scaleX = ratio of natural scene width to screen width
          // holdW = natural width of product image
          const productScale = holdW ? (overlayWdisp * scaleX) / holdW : 1;
          obj = {
            // earrings use sticky_point (relative) instead of points (absolute px)
            sticky_point: (points && points.length > 0) ? [points[0].x, points[0].y] : [0, 0],
            padding: 0,
            scale: productScale,
            category: 'Earrings',
            img_path: (sceneName && sceneName.trim()) ? `data/scenes/${sceneName.trim()}.webp` : '',
            product_placement_coordinate: (placement && placement.length > 0) ? placement[0] : [0, 0]
          };
        } else {
          obj = {
            points: pts,
            padding: 0,
            category: (productType === 'watches' ? 'Watches' : (productType === 'earrings' ? 'Earrings' : 'Jewelry')),
            img_path: (sceneName && sceneName.trim()) ? `data/scenes/${sceneName.trim()}.webp` : '',
            product_placement_coordinates: placement
          };
        }
      }
      const fd = new FormData();
      fd.append('scene_name', (sceneName || '').trim());
      fd.append('index', String(parseInt(String(sceneIndex), 10)));
      fd.append('json_text', JSON.stringify(obj));
      fd.append('image', uploadedImageFile, uploadedImageFile.name);
      fd.append('scene_blend', sceneFile, `${(sceneName || '').trim()}_${sceneIndex}.blend`);
      // eslint-disable-next-line no-console
      console.log('[SaveScene] POST', url, { scene_name: sceneName, index: sceneIndex, json: obj });
      const resp = await fetch(url, { method: 'POST', body: fd });
      // eslint-disable-next-line no-console
      console.log('[SaveScene] resp', resp.status);
      if (!resp.ok) {
        const txt = await resp.text();
        setSaveMessage(`Failed to save scene: ${resp.status} ${txt}`);
        return;
      }
      const data = await resp.json();
      const p = data?.paths || {};
      const imgP = p.image ? String(p.image) : '—';
      const blendP = p.blend ? String(p.blend) : '—';
      const jsonP = p.json ? String(p.json) : '—';
      setSaveMessage(`Saved → image: ${imgP}, blend: ${blendP}, json: ${jsonP}`);
    } catch (e) {
      setSaveMessage(`Error saving scene: ${e}`);
    } finally {
      setSaving(false);
    }
  };

  const canRender = Boolean(
    productType &&
    sceneFile &&
    productFile &&
    (
      (productType === 'necklaces' ? renderMode : true) &&
      ((productType === 'watches' || productType === 'bracelets') ? wristWidth : true)
    )
  );

  const handleRender = async () => {
    if (!canRender) return;
    // Build request preview instead of sending (new unified /render expects product_file + scene_file)
    const params = new URLSearchParams();
    const endpoint = '/render-unified';
    // Prefer local API during development
    setApiBase(config.serverUrl);
    // Map UI productType to backend product_type
    let productTypeUnified = '';
    if (productType === 'watches') productTypeUnified = 'watch';
    if (productType === 'bracelets') productTypeUnified = 'bracelet';
    if (productType === 'necklaces') productTypeUnified = 'necklace';
    if (productType === 'rings') productTypeUnified = 'ring';
    if (productType === 'earrings') productTypeUnified = 'earrings';
    if (productTypeUnified) {
      params.append('product_type', productTypeUnified);
    }
    // Minimal required params
    if (productType === 'watches' || productType === 'bracelets') {
      params.append('wrist_size', String(wristWidth));
    }
    if (productType === 'necklaces') {
      params.append('mode', renderMode);
    }
    // default product option removed

    const baseUrl = `${config.serverUrl}${endpoint}`;
    // Build Python requests preview (multi-file upload)
    const paramsPy = [];
    if (productTypeUnified) {
      paramsPy.push(`    'product_type': '${productTypeUnified}',`);
    }
    if (productType === 'watches' || productType === 'bracelets') {
      paramsPy.push(`    'wrist_size': ${parseInt(String(wristWidth || '0'), 10)},`);
    }
    if (productType === 'necklaces') {
      paramsPy.push(`    'mode': '${renderMode}',`);
    }
    if (productType === 'watches') {
      paramsPy.push(`    'product_title': 'Watch POM',`);
    } else if (productType === 'bracelets') {
      paramsPy.push(`    'product_title': 'Bracelet POM',`);
    } else if (productType === 'necklaces') {
      paramsPy.push(`    'product_title': 'Necklace POM',`);
    } else if (productType === 'rings') {
      paramsPy.push(`    'product_title': 'Ring POM',`);
    } else if (productType === 'earrings') {
      paramsPy.push(`    'product_title': 'Earrings POM',`);
    }

    const py = [
      'import requests',
      '',
      `url = '${baseUrl}'`,
      'headers = {"accept": "application/json"}',
      'params = {',
      ...paramsPy,
      '}',
      'files = [',
      `    ('product_file', open('./${productFile ? productFile.name : 'product.blend'}', 'rb')),`,
      `    ('scene_file', open('./${sceneFile ? sceneFile.name : 'scene.blend'}', 'rb')),`,
      ']',
      '',
      '',
      'response = requests.post(url, params=params, files=files, headers=headers, verify=False)',
      'print(response.status_code)',
      'print(response.text)'
    ].join('\n');
    setRequestPreview(py);
    // Also send the request to backend
    try {
      setSubmitting(true);
      const url = `${baseUrl}?${params.toString()}`;
      const formData = new FormData();
      formData.append('product_file', productFile, productFile?.name);
      formData.append('scene_file', sceneFile, sceneFile?.name);
      const resp = await fetch(url, {
        method: 'POST',
        body: formData
      });
      // eslint-disable-next-line no-console
      console.log('Render response', resp.status);
      try {
        const json = await resp.json();
        if (json && json.session_id) {
          setLastSessionId(json.session_id);
          setCurrentSessionId(json.session_id);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Non-JSON response from render', e);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to send render request', e);
    } finally {
      setSubmitting(false);
    }
  };

  const wristOptions = productType === 'watches'
    ? [55, 65]
    : (productType === 'bracelets' ? [65, 55] : []);

  const mapEventToNormalized = (clientX, clientY) => {
    const el = containerRef.current || imgRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    if (x < 0 || y < 0 || x > 1 || y > 1) return null;
    return { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) };
  };

  const handleImageClick = (e) => {
    if (!selectedImage || isDragging) return;
    const pos = mapEventToNormalized(e.clientX, e.clientY);
    if (!pos) return;
    const newPoint = { id: Date.now(), x: pos.x, y: pos.y };
    setPoints((prev) => [...prev, newPoint]);
    setSelectedPointId(newPoint.id);
  };

  const deletePoint = (id) => {
    setPoints((prev) => prev.filter((p) => p.id !== id));
    if (selectedPointId === id) setSelectedPointId(null);
  };

  const startDragPoint = (id) => {
    setSelectedPointId(id);
    setIsDragging(true);
    setDragPointId(id);
  };
  const endDragPoint = () => {
    setIsDragging(false);
    setDragPointId(null);
  };
  const onGlobalMouseMove = (e) => {
    if (!isDragging || dragPointId == null) return;
    const pos = mapEventToNormalized(e.clientX, e.clientY);
    if (!pos) return;
    setPoints((prev) => prev.map((p) => (p.id === dragPointId ? { ...p, x: pos.x, y: pos.y } : p)));
  };
  useEffect(() => {
    const move = (e) => onGlobalMouseMove(e);
    const up = () => endDragPoint();
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [isDragging, dragPointId]);

  useEffect(() => {
    return () => {
      if (uploadedImagePreviewUrl) {
        URL.revokeObjectURL(uploadedImagePreviewUrl);
      }
    };
  }, [uploadedImagePreviewUrl]);

  // Auto-pick first holdout image from the first images list
  useEffect(() => {
    if (!images || !images.length) {
      setHoldoutSrc(null);
      return;
    }
    const found = images.find((u) => /holdout/i.test(String(u)));
    setHoldoutSrc(found || null);
  }, [images]);

  // Setup and cleanup global mouse handlers for overlay drag/resize
  useEffect(() => {
    const onMove = (e) => {
      if (!uploadContainerRef.current) return;
      const rect = uploadContainerRef.current.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return;
      if (isOverlayDragging && resizeDragStartRef.current) {
        const s = resizeDragStartRef.current;
        const dx = e.clientX - s.startX;
        const dy = e.clientY - s.startY;
        // Allow going outside boundaries for bracelets, watches, and earrings
        let newLeft = s.startLeft + dx;
        let newTop = s.startTop + dy;
        if (productType !== 'bracelets' && productType !== 'watches' && productType !== 'earrings') {
          newLeft = Math.max(0, Math.min(rect.width - s.startWidth, newLeft));
          newTop = Math.max(0, Math.min(rect.height - (s.startWidth / (holdoutNaturalRatio || 1)), newTop));
        }
        setOverlayBoxPx((prev) => ({
          left: newLeft,
          top: newTop,
          width: s.startWidth
        }));
      } else if (activeResizeHandle && resizeDragStartRef.current) {
        const s = resizeDragStartRef.current;
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        // Anchor is fixed based on starting box and handle
        const startW = s.startWidth;
        const startH = s.startHeight;
        const startL = s.startLeft;
        const startT = s.startTop;
        let ax = 0; let ay = 0; let sxSign = 1; let sySign = 1;
        if (s.handle === 'se') { ax = startL; ay = startT; sxSign = 1; sySign = 1; }
        if (s.handle === 'ne') { ax = startL; ay = startT + startH; sxSign = 1; sySign = -1; }
        if (s.handle === 'sw') { ax = startL + startW; ay = startT; sxSign = -1; sySign = 1; }
        if (s.handle === 'nw') { ax = startL + startW; ay = startT + startH; sxSign = -1; sySign = -1; }
        const vx = px - ax;
        const vy = py - ay;
        const denom = 1 + 1 / (holdoutNaturalRatio * holdoutNaturalRatio);
        let w = (sxSign * vx + (sySign / holdoutNaturalRatio) * vy) / denom;
        // Constrain width
        let maxW;
        if (productType === 'bracelets' || productType === 'watches' || productType === 'earrings') {
          // Allow these categories to grow beyond container bounds
          maxW = Math.max(rect.width, rect.height * holdoutNaturalRatio) * 3;
        } else {
          // Other categories: keep prior generous bound
          maxW = Math.min(rect.width, rect.height * holdoutNaturalRatio) * 1.5;
        }
        const minW = 10;
        if (!Number.isFinite(w)) w = startW;
        w = Math.max(minW, Math.min(maxW, Math.abs(w)));
        const h = w / holdoutNaturalRatio;
        // Compute new left/top from anchor and signs
        let newLeft = ax + (sxSign === -1 ? -w : 0);
        let newTop = ay + (sySign === -1 ? -h : 0);
        // Clamp within container unless bracelets/watches/earrings/necklaces/rings (can go outside)
        if (productType !== 'bracelets' && productType !== 'watches' && productType !== 'earrings' && productType !== 'necklaces' && productType !== 'rings') {
          newLeft = Math.max(0, Math.min(rect.width - w, newLeft));
          newTop = Math.max(0, Math.min(rect.height - h, newTop));
        }
        setOverlayBoxPx({ left: newLeft, top: newTop, width: w });
      }
    };
    const onUp = () => {
      setIsOverlayDragging(false);
      setActiveResizeHandle(null);
      resizeDragStartRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isOverlayDragging, activeResizeHandle, holdoutNaturalRatio]);

  return (
    <PageContainer>
      <Sidebar navigate={navigate} />
      <ContentContainer>
        <Header>Add new POM scene</Header>
        {/* Form fields and render button come first now */}
        <Box sx={{ width: '100%', maxWidth: 300, alignSelf: 'flex-start' }}>
          <FormControl size="small" sx={{ mb: 2, width: 240 }}>
            <InputLabel id="product-type-label">Product type</InputLabel>
            <Select
              labelId="product-type-label"
              label="Product type"
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
            >
              <MenuItem value="bracelets">Bracelets</MenuItem>
              <MenuItem value="earrings">Earrings</MenuItem>
              <MenuItem value="necklaces">Necklaces</MenuItem>
              <MenuItem value="rings">Rings</MenuItem>
              <MenuItem value="watches">Watches</MenuItem>
            </Select>
          </FormControl>

          {(productType === 'watches' || productType === 'bracelets') && (
            <FormControl size="small" sx={{ mb: 2, width: 240 }}>
              <InputLabel id="wrist-width-label">Wrist width (mm)</InputLabel>
              <Select
                labelId="wrist-width-label"
                label="Wrist width (mm)"
                value={wristWidth}
                onChange={(e) => setWristWidth(e.target.value)}
              >
                {wristOptions.map((v) => (
                  <MenuItem key={String(v)} value={String(v)}>{v}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {productType === 'necklaces' && (
            <FormControl size="small" sx={{ mb: 2, width: 240 }}>
              <InputLabel id="render-mode-label">Rendering mode</InputLabel>
              <Select
                labelId="render-mode-label"
                label="Rendering mode"
                value={renderMode}
                onChange={(e) => setRenderMode(e.target.value)}
              >
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="male">Male</MenuItem>
              </Select>
            </FormControl>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {/* Upload scene (.blend) */}
            <input
              id="upload-scene-blend"
              type="file"
              accept=".blend"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                setSceneFile(f || null);
              }}
            />
            <label htmlFor="upload-scene-blend">
              <Button variant="contained" size="small" component="span">
                Upload scene (.blend)
              </Button>
            </label>
            {sceneFile && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {sceneFile.name}
              </Typography>
            )}

            {/* Use default product toggle removed */}

            {/* Upload product (.blend) - optional */}
            <input
              id="upload-product-blend"
              type="file"
              accept=".blend"
              style={{ display: 'none' }}
              disabled={false}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                setProductFile(f || null);
              }}
            />
            <label htmlFor="upload-product-blend">
              <Button variant="outlined" size="small" component="span" disabled={false}>
                Upload product (.blend)
              </Button>
            </label>
            {productFile && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {productFile.name}
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={!canRender || submitting}
              sx={{ mt: 2, width: 140 }}
              onClick={handleRender}
            >
              {submitting ? 'Sending…' : 'Render'}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              disabled={!currentSessionId}
              sx={{ mt: 1, width: 140 }}
              onClick={handleShowImages}
            >
              Show images
            </Button>
          </Box>
          {/*
          {requestPreview && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Python requests (preview)</Typography>
              {lastSessionId && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  session_id: {lastSessionId}
                </Typography>
              )}
              {currentSessionId && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  images_path: {(apiBase || window.location.origin) + `/renders/${currentSessionId}/`}
                </Typography>
              )}
              <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', fontSize: 12 }}>
                {requestPreview}
              </Box>
            </Paper>
          )}
          */}
        </Box>
        {/* Images viewer from session moved below the form */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 4 }}>
          {/*
          <Box sx={{ position: 'absolute', top: 64, right: 24, maxWidth: 520 }}>
            {currentSessionId && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Session debug</Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>session_id: {currentSessionId}</Typography>
                <Typography variant="caption" sx={{ display: 'block' }}>endpoint: {sessionImagesQueryUrl || '(pending)'}</Typography>
                {sessionFsBaseAbs && (
                  <Typography variant="caption" sx={{ display: 'block' }}>server base: {sessionFsBaseAbs}</Typography>
                )}
                {sessionFsTargetAbs && (
                  <Typography variant="caption" sx={{ display: 'block' }}>server target: {sessionFsTargetAbs}</Typography>
                )}
              </Paper>
            )}
          </Box>
          */}
          {/* Thumbnails */}
          <Box sx={{
            width: 120,
            maxHeight: 420,
            overflowY: 'auto',
            borderRight: '1px solid rgba(0,0,0,0.08)',
            pr: 1
          }}>
            {images.map((src, idx) => {
              const isActive = src === selectedImage;
              return (
                <Box
                  key={`${src}_${idx}`}
                  sx={{
                    mb: 1,
                    p: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: isActive ? '2px solid #1976d2' : '1px solid rgba(0,0,0,0.12)',
                    backgroundColor: isActive ? 'rgba(25,118,210,0.06)' : 'transparent'
                  }}
                  onClick={() => setSelectedImage(src)}
                  title={src}
                >
                  <Box
                    component="img"
                    src={src}
                    alt={`thumb-${idx}`}
                    sx={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }}
                  />
                </Box>
              );
            })}
            {!images.length && (
              <Typography variant="caption" sx={{ color: imagesError ? 'error.main' : 'text.secondary' }}>
                {imagesError || 'No images.'}
              </Typography>
            )}
          </Box>
          {/* Main image */}
          <Box sx={{ flex: 1, minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {selectedImage ? (
              <Box sx={{ width: '100%', height: '100%', maxHeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ position: 'relative', display: 'inline-block' }} ref={containerRef}>
                  <Box
                    component="img"
                    src={selectedImage}
                    alt="selected"
                    ref={imgRef}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 600,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid rgba(0,0,0,0.12)'
                    }}
                    onLoad={(e) => {
                      const el = e.currentTarget;
                      setImgNaturalSize({ w: el.naturalWidth || 0, h: el.naturalHeight || 0 });
                    }}
                  />
                  {/* Click capture overlay sized exactly to the displayed image */}
                  <Box
                    onClick={handleImageClick}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      cursor: isDragging ? 'grabbing' : 'crosshair'
                    }}
                  />
                  {/* Points overlay sized exactly to the displayed image */}
                  <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    {points.map((p) => (
                      <Box
                        key={p.id}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          startDragPoint(p.id);
                        }}
                        title={`${Math.round(p.x * (imgNaturalSize.w || 0))}x${Math.round(p.y * (imgNaturalSize.h || 0))}`}
                        sx={{
                          position: 'absolute',
                          left: `${p.x * 100}%`,
                          top: `${p.y * 100}%`,
                          transform: 'translate(-50%, -50%)',
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: selectedPointId === p.id ? '#d32f2f' : '#1976d2',
                          border: '2px solid #fff',
                          boxShadow: '0 0 0 1px rgba(0,0,0,0.2)',
                          pointerEvents: 'auto',
                          cursor: isDragging && dragPointId === p.id ? 'grabbing' : 'grab'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Select an image
              </Typography>
            )}
          </Box>
          {/* Points panel on the right */}
          <Box sx={{ width: 300, minHeight: 420, borderLeft: '1px solid rgba(0,0,0,0.08)', pl: 2 }}>
            <List dense sx={{ width: '100%', maxHeight: 420, overflowY: 'auto' }}>
              {points.map((p, idx) => (
                <Box key={p.id}>
                  <ListItem
                    selected={selectedPointId === p.id}
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete" onClick={() => deletePoint(p.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="subtitle2">Point #{idx + 1}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {`${Math.round(p.x * (imgNaturalSize.w || 0))} x ${Math.round(p.y * (imgNaturalSize.h || 0))} px`}
                      </Typography>
                    </Box>
                  </ListItem>
                  <Divider component="li" />
                </Box>
              ))}
              {points.length === 0 && (
                <ListItem>
                  <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} primary="No points yet. Click on the image to add points." />
                </ListItem>
              )}
            </List>
            {/* JSON field under the points list */}
            <Paper variant="outlined" sx={{ mt: 2, p: 1.5 }}>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  maxHeight: 200,
                  overflowY: 'auto'
                }}
              >
                {JSON.stringify({
                  points: points.map((p) => ([
                    Math.round(p.x * (imgNaturalSize.w || 0)),
                    Math.round(p.y * (imgNaturalSize.h || 0))
                  ]))
                }, null, 2)}
              </Box>
            </Paper>
          </Box>

        </Box>
        {/* Postprocessing controls and header */}
        <Box sx={{ mt: 2, alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {productType === 'rings' && (
            <TextField
              size="small"
              label="Padding"
              type="number"
              inputProps={{ step: '0.01', min: '0', max: '1' }}
              sx={{ width: 120 }}
              value={postprocPadding}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (Number.isFinite(val)) {
                  setPostprocPadding(val);
                } else {
                  setPostprocPadding(0.2);
                }
              }}
            />
          )}
          <Button variant="contained" color="secondary" size="small" onClick={handlePostprocess} disabled={procLoading}>
            {procLoading ? 'Processing…' : 'Postprocess'}
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Postprocessing</Typography>
        </Box>
        {/* Postprocessed images viewer */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 2 }}>
          {/* Thumbnails */}
          <Box sx={{
            width: 120,
            maxHeight: 420,
            overflowY: 'auto',
            borderRight: '1px solid rgba(0,0,0,0.08)',
            pr: 1
          }}>
            {procImages.map((src, idx) => {
              const isActive = src === procSelected;
              return (
                <Box
                  key={`proc_${src}_${idx}_${procVersion}`}
                  sx={{
                    mb: 1,
                    p: 0.5,
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: isActive ? '2px solid #7b1fa2' : '1px solid rgba(0,0,0,0.12)',
                    backgroundColor: isActive ? 'rgba(123,31,162,0.06)' : 'transparent'
                  }}
                  onClick={() => setProcSelected(src)}
                  title={src}
                >
                  <Box
                    component="img"
                    src={`${src}?v=${procVersion}`}
                    alt={`proc-thumb-${idx}`}
                    sx={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }}
                  />
                </Box>
              );
            })}
            {!procImages.length && (
              <Typography variant="caption" sx={{ color: procError ? 'error.main' : 'text.secondary' }}>
                {procError || 'No images.'}
              </Typography>
            )}
          </Box>
          {/* Main image */}
          <Box sx={{ flex: 1, minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {procSelected ? (
              <Box
                component="img"
                src={`${procSelected}?v=${procVersion}`}
                alt="postprocessed"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 600,
                  objectFit: 'contain',
                  borderRadius: 1,
                  border: '1px solid rgba(0,0,0,0.12)'
                }}
              />
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No postprocessed image selected
              </Typography>
            )}
          </Box>
        </Box>
        {/* Upload custom image and preview */}
        <Box sx={{ mt: 4, width: '100%', alignSelf: 'flex-start' }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>Upload image</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              id="upload-custom-image"
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (!f) {
                  setUploadedImageFile(null);
                  if (uploadedImagePreviewUrl) URL.revokeObjectURL(uploadedImagePreviewUrl);
                  setUploadedImagePreviewUrl(null);
                  return;
                }
                setUploadedImageFile(f);
                if (uploadedImagePreviewUrl) URL.revokeObjectURL(uploadedImagePreviewUrl);
                const url = URL.createObjectURL(f);
                setUploadedImagePreviewUrl(url);
              }}
            />
            <label htmlFor="upload-custom-image">
              <Button variant="contained" size="small" component="span">
                Choose image
              </Button>
            </label>
            {uploadedImageFile && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {uploadedImageFile.name}
              </Typography>
            )}
            {uploadedImageFile && (
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => {
                  setUploadedImageFile(null);
                  if (uploadedImagePreviewUrl) URL.revokeObjectURL(uploadedImagePreviewUrl);
                  setUploadedImagePreviewUrl(null);
                }}
              >
                Clear
              </Button>
            )}
          </Box>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 2 }}>
            {uploadedImagePreviewUrl ? (
              <>
                <Box sx={{ position: 'relative', display: 'inline-block', userSelect: 'none' }} ref={uploadContainerRef}>
                  <Box
                    component="img"
                    src={uploadedImagePreviewUrl}
                    alt="uploaded-preview"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 600,
                      objectFit: 'contain',
                      display: 'block',
                      borderRadius: 1,
                      border: '1px solid rgba(0,0,0,0.12)'
                    }}
                    onLoad={(e) => {
                      // Initialize overlay size and position roughly centered and capture base image sizes
                      const imgEl = e.currentTarget;
                      const rect = uploadContainerRef.current ? uploadContainerRef.current.getBoundingClientRect() : null;
                      if (!rect) return;
                      const naturalW = imgEl.naturalWidth || 1;
                      const naturalH = imgEl.naturalHeight || 1;
                      setBaseImageNaturalSize({ w: Math.round(naturalW), h: Math.round(naturalH) });
                      setBaseImageDisplaySize({ w: rect.width, h: rect.height });
                      const initW = Math.max(60, Math.min(rect.width * 0.3, rect.width - 20));
                      const initH = initW / (holdoutNaturalRatio || 1);
                      const left = Math.max(0, (rect.width - initW) / 2);
                      const top = Math.max(0, (rect.height - initH) / 2);
                      setOverlayBoxPx({ left, top, width: initW });
                    }}
                  />
                  {/* Overlay: holdout image draggable/resizable */}
                  {holdoutSrc && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${overlayBoxPx.left}px`,
                        top: `${overlayBoxPx.top}px`,
                        width: `${overlayBoxPx.width}px`,
                        height: `${(overlayBoxPx.width / (holdoutNaturalRatio || 1))}px`,
                        cursor: isOverlayDragging ? 'grabbing' : 'grab',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.2)',
                        border: '1px dashed rgba(123,31,162,0.7)',
                        borderRadius: 0.5,
                        backgroundColor: 'transparent'
                      }}
                      onMouseDown={(e) => {
                        // Prevent starting drag when clicking on handles
                        const target = e.target;
                        if (target && target.getAttribute && target.getAttribute('data-handle')) return;
                        const rect = uploadContainerRef.current ? uploadContainerRef.current.getBoundingClientRect() : null;
                        if (!rect) return;
                        const height = overlayBoxPx.width / (holdoutNaturalRatio || 1);
                        resizeDragStartRef.current = {
                          type: 'drag',
                          startX: e.clientX,
                          startY: e.clientY,
                          startLeft: overlayBoxPx.left,
                          startTop: overlayBoxPx.top,
                          startWidth: overlayBoxPx.width,
                          startHeight: height,
                          containerRect: rect
                        };
                        setIsOverlayDragging(true);
                      }}
                    >
                      <Box
                        component="img"
                        src={holdoutSrc}
                        alt="holdout-overlay"
                        sx={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none', userSelect: 'none' }}
                        onLoad={(e) => {
                          const img = e.currentTarget;
                          const w = img.naturalWidth || 1;
                          const h = img.naturalHeight || 1;
                          setHoldoutNaturalRatio(Math.max(0.0001, w / h));
                          setHoldoutNaturalSize({ w, h });
                        }}
                      />
                      {/* Corner handles for proportional resize */}
                      {['nw', 'ne', 'se', 'sw'].map((h) => {
                        const posStyle =
                          h === 'nw' ? { left: -6, top: -6, cursor: 'nwse-resize' } :
                            h === 'ne' ? { right: -6, top: -6, cursor: 'nesw-resize' } :
                              h === 'se' ? { right: -6, bottom: -6, cursor: 'nwse-resize' } :
                                { left: -6, bottom: -6, cursor: 'nesw-resize' };
                        return (
                          <Box
                            key={h}
                            data-handle={h}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              const rect = uploadContainerRef.current ? uploadContainerRef.current.getBoundingClientRect() : null;
                              if (!rect) return;
                              const height = overlayBoxPx.width / (holdoutNaturalRatio || 1);
                              resizeDragStartRef.current = {
                                type: 'resize',
                                handle: h,
                                startX: e.clientX,
                                startY: e.clientY,
                                startLeft: overlayBoxPx.left,
                                startTop: overlayBoxPx.top,
                                startWidth: overlayBoxPx.width,
                                startHeight: height,
                                containerRect: rect
                              };
                              setActiveResizeHandle(h);
                            }}
                            sx={{
                              position: 'absolute',
                              width: 12,
                              height: 12,
                              borderRadius: 0.5,
                              backgroundColor: '#7b1fa2',
                              border: '2px solid #fff',
                              boxShadow: '0 0 0 1px rgba(0,0,0,0.2)',
                              ...posStyle
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
                {/* Product placement coordinates JSON next to uploaded image */}
                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 260, maxWidth: 360 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>product_placement_coordinates</Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      fontSize: 12,
                      maxHeight: 560,
                      overflowY: 'auto'
                    }}
                  >
                    {(() => {
                      // For rings: map overlay anchors to absolute pixels on original base image
                      // For bracelets, watches, and necklaces: map labeled points' relative coords inside the overlay box (updates on drag/resize)
                      // For other non-ring categories: use labeled base-image points scaled to original size
                      const natW = baseImageNaturalSize.w || 0;
                      const natH = baseImageNaturalSize.h || 0;
                      if (productType === 'rings' || productType === 'bracelets' || productType === 'watches' || productType === 'necklaces' || productType === 'earrings') {
                        const dispW = baseImageDisplaySize.w || 0;
                        const dispH = baseImageDisplaySize.h || 0;
                        const scaleX = dispW ? (natW / dispW) : 1;
                        const scaleY = dispH ? (natH / dispH) : 1;
                        const overlayWdisp = overlayBoxPx.width || 0;
                        const overlayHdisp = (overlayBoxPx.width || 0) / (holdoutNaturalRatio || 1);
                        const leftDisp = overlayBoxPx.left || 0;
                        const topDisp = overlayBoxPx.top || 0;
                        const mapped = points.slice(0, 2).map((p) => {
                          const xd = leftDisp + (p?.x || 0) * overlayWdisp;
                          const yd = topDisp + (p?.y || 0) * overlayHdisp;
                          return [Math.round(xd * scaleX), Math.round(yd * scaleY)];
                        });
                        return JSON.stringify({ product_placement_coordinates: mapped }, null, 2);
                      } else {
                        const labelPts = points.slice(0, 2).map((p) => ([
                          Math.round(p.x * natW),
                          Math.round(p.y * natH)
                        ]));
                        return JSON.stringify({ product_placement_coordinates: labelPts }, null, 2);
                      }
                    })()}
                  </Box>
                </Paper>
                {/* Scene fields and generated JSON (no Save button here) */}
                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 300, maxWidth: 420 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Scene</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <TextField
                      size="small"
                      label="Scene name"
                      value={sceneName}
                      onChange={(e) => setSceneName(e.target.value)}
                    />
                    <TextField
                      size="small"
                      label="Index"
                      type="number"
                      value={sceneIndex}
                      onChange={(e) => setSceneIndex(e.target.value)}
                      sx={{ width: 120 }}
                    />
                  </Box>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      fontSize: 12,
                      maxHeight: 560,
                      overflowY: 'auto'
                    }}
                  >
                    {(() => {
                      // points from main image (first json)
                      const pts = points.map((p) => ([
                        Math.round(p.x * (imgNaturalSize.w || 0)),
                        Math.round(p.y * (imgNaturalSize.h || 0))
                      ]));
                      // placement: rings -> overlay anchors; bracelets/watches/necklaces -> mapped labeled points; others -> labeled points scaled to uploaded image's original size
                      const natW = baseImageNaturalSize.w || 0;
                      const natH = baseImageNaturalSize.h || 0;
                      let placement = [];
                      if (productType === 'rings') {
                        const dispW = baseImageDisplaySize.w || 0;
                        const dispH = baseImageDisplaySize.h || 0;
                        const scaleX = dispW ? (natW / dispW) : 1;
                        const scaleY = dispH ? (natH / dispH) : 1;
                        const overlayWdisp = overlayBoxPx.width || 0;
                        const overlayHdisp = (overlayBoxPx.width || 0) / (holdoutNaturalRatio || 1);
                        const leftDisp = overlayBoxPx.left || 0;
                        const topDisp = overlayBoxPx.top || 0;
                        const holdW = holdoutNaturalSize.w || 0;
                        const holdH = holdoutNaturalSize.h || 0;
                        const sx = holdW ? (overlayWdisp / holdW) : 0;
                        const sy = holdH ? (overlayHdisp / holdH) : 0;
                        placement = points.slice(0, 2).map((p) => {
                          const xd = leftDisp + (p?.x || 0) * overlayWdisp;
                          const yd = topDisp + (p?.y || 0) * overlayHdisp;
                          return [Math.round(xd * scaleX), Math.round(yd * scaleY)];
                        });
                      } else if (productType === 'bracelets' || productType === 'watches' || productType === 'necklaces' || productType === 'earrings') {
                        const dispW = baseImageDisplaySize.w || 0;
                        const dispH = baseImageDisplaySize.h || 0;
                        const scaleX = dispW ? (natW / dispW) : 1;
                        const scaleY = dispH ? (natH / dispH) : 1;
                        const overlayWdisp = overlayBoxPx.width || 0;
                        const overlayHdisp = (overlayBoxPx.width || 0) / (holdoutNaturalRatio || 1);
                        const leftDisp = overlayBoxPx.left || 0;
                        const topDisp = overlayBoxPx.top || 0;
                        placement = points.slice(0, 2).map((p) => {
                          const xd = leftDisp + (p?.x || 0) * overlayWdisp;
                          const yd = topDisp + (p?.y || 0) * overlayHdisp;
                          return [Math.round(xd * scaleX), Math.round(yd * scaleY)];
                        });
                      } else {
                        placement = points.slice(0, 2).map((p) => ([
                          Math.round(p.x * natW),
                          Math.round(p.y * natH)
                        ]));
                      }
                      let obj = {};
                      if (productType === 'rings') {
                        const padLocal = Number.isFinite(Number(postprocPadding)) ? Number(postprocPadding) : 0.2;
                        obj = {
                          points: pts,
                          padding: padLocal,
                          category: 'Jewelry',
                          img_path: sceneName ? `data/scenes/${sceneName}.webp` : '',
                          product_placement_coordinates: placement
                        };
                      } else if (productType === 'bracelets') {
                        obj = {
                          points: pts,
                          category: 'Bracelets',
                          size: parseInt(String(wristWidth || '0'), 10) || 0,
                          img_path: sceneName ? `data/scenes/${sceneName}.webp` : '',
                          product_placement_coordinates: placement
                        };
                      } else if (productType === 'watches') {
                        obj = {
                          points: pts,
                          category: 'Watches',
                          size: parseInt(String(wristWidth || '0'), 10) || 0,
                          img_path: sceneName ? `data/scenes/${sceneName}.webp` : '',
                          product_placement_coordinates: placement
                        };
                      } else if (productType === 'necklaces') {
                        obj = {
                          sticky_point: [0.5, 0.25],
                          necklace_points: pts,
                          product_placement_coordinates: placement,
                          category: 'Necklaces',
                          img_path: sceneName ? `data/scenes/${sceneName}.webp` : ''
                        };
                      } else if (productType === 'earrings') {
                        // Earrings: single sticky point in relative coords, single placement coordinate in absolute px, and scale
                        const p0 = points[0];
                        const stickyRel = p0 ? [Number((p0.x).toFixed(6)), Number((p0.y).toFixed(6))] : [0, 0];
                        const placementSingle = Array.isArray(placement) && placement.length ? placement[0] : [0, 0];
                        // Re-calculate scale components if needed for this scope
                        const holdWLocal = holdoutNaturalSize.w || 0;
                        const dispW = baseImageDisplaySize.w || 0;
                        const scaleX = dispW ? (natW / dispW) : 1;
                        const productScale = holdWLocal ? (overlayBoxPx.width * scaleX) / holdWLocal : 1;
                        obj = {
                          sticky_point: stickyRel,
                          category: 'Earrings',
                          img_path: sceneName ? `data/scenes/${sceneName}.webp` : '',
                          product_placement_coordinate: placementSingle,
                          scale: productScale
                        };
                      } else {
                        const catLocal = (
                          productType === 'necklaces' ? 'Necklaces' :
                            (productType === 'watches' ? 'Watches' :
                              (productType === 'earrings' ? 'Earrings' : 'Jewelry')));
                        obj = {
                          points: pts,
                          padding: 0,
                          category: catLocal,
                          img_path: sceneName ? `data/scenes/${sceneName}.webp` : '',
                          product_placement_coordinates: placement
                        };
                      }
                      return JSON.stringify(obj, null, 2);
                    })()}
                  </Box>
                </Paper>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No image chosen
              </Typography>
            )}
          </Box>
        </Box>
        {/* Final Save button and inline notification at the end of the page */}
        <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2, alignSelf: 'flex-start' }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            disabled={
              !uploadedImageFile ||
              !sceneFile ||
              !sceneName ||
              !sceneName.trim() ||
              !sceneIndex ||
              ((productType === 'watches' || productType === 'bracelets') && !wristWidth) ||
              saving
            }
            onClick={handleSaveScene}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          {saveMessage ? (
            <Typography variant="body2" sx={{ color: saveMessage.startsWith('Saved') ? 'success.main' : 'error.main' }}>
              {saveMessage}
            </Typography>
          ) : null}
        </Box>
      </ContentContainer>
    </PageContainer>
  );
};

export default PomSceneAddPage;
